import './LoiComparisonSummary.scss';
import React, { useState } from 'react';
import { VvConnectFeesSection, getFeeRows } from './VvConnectFeesSection';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPlus, faMinus } from '@fortawesome/free-solid-svg-icons';
import { LoiComparisonFormDto } from '../../../../slices/marketplace/loi-comparison/models/loi-comparison-form.model';
import { LevelOfService } from '../../../../slices/marketplace/timeline/models/enums/level-of-service.enum';
import BuyerProfilePreviewPopup from '../../../../components/BuyerProfilePreviewPopup/BuyerProfilePreviewPopup';
import InfoTooltip from '../../../../components/info-tooltip/info-tooltip';
import {
    PriceRow,
    computeTotalValues,
    getFieldComment,
    formatBoolean,
    formatCell,
    buildCashAtCloseRow,
    buildDeferredFixedRow,
    buildDeferredVariableRow,
    buildEarnoutRow,
    buildSyntheticJvRow,
    buildJvEquityRow,
    buildCorporateEquityRow,
    buildOtherRow,
} from './LoiComparisonRows';

const TAIL_COLUMN_HEADERS = [
    'Fees',
    'Net of Fees',
    'Employee Portion',
    'Net to Owner',
];

const PRICE_ROWS: {
    label: string;
    getCells: (dto: LoiComparisonFormDto) => import('./LoiComparisonRows').RowCellValues;
    indented?: boolean;
}[] = [
    { label: 'Cash at Close', getCells: buildCashAtCloseRow },
    { label: 'Deferred Fixed Payments', getCells: buildDeferredFixedRow },
    { label: 'Deferred Variable Payments', getCells: buildDeferredVariableRow },
    { label: 'Earnout', getCells: buildEarnoutRow, indented: true },
    { label: 'Synthetic JV Equity', getCells: buildSyntheticJvRow, indented: true },
    { label: 'JV Equity', getCells: buildJvEquityRow },
    { label: 'Corporate Equity', getCells: buildCorporateEquityRow },
    { label: 'Other', getCells: buildOtherRow },
];

const CONTRACT_LABELS = [
    { label: 'Lab Contract(s)', key: 'labContract' as const, explanationKey: 'labContractExplanation' as const },
    { label: 'Operating Leases', key: 'operatingLeases' as const, explanationKey: 'operatingLeasesExplanation' as const },
    { label: 'Capital Leases', key: 'capitalLeases' as const, explanationKey: 'capitalLeasesExplanation' as const },
    { label: 'Real-Estate Lease', key: 'realEstateLease' as const, explanationKey: 'realEstateLeaseExplanation' as const },
];

const NON_COMP_LABELS = [
    { label: 'Distance: Radius (miles)', key: 'distanceRadiusMiles' as const },
    { label: 'Duration: Years from Closing', key: 'durationYearsFromClosing' as const },
    { label: 'Duration: Years from Employment', key: 'durationYearsFromEmployment' as const },
];

const COLLAPSIBLE_COLUMN_TOKEN = '__pct:';

const LabelsColumn = () => {
    return (
        <div className="loi-labels-column">
            <table>
                <thead>
                    <tr>
                        <th className="col-label section-title">Purchase Price</th>
                    </tr>
                    <tr className="sub-col-header-row">
                        <th className="col-label section-title">Consideration Summary</th>
                    </tr>
                </thead>
                <tbody>
                    {PRICE_ROWS.map(({ label, indented }) => (
                        <tr key={label}>
                            <td className={`col-label${indented ? ' col-label--indented' : ''}`}>{label}</td>
                        </tr>
                    ))}
                    <tr className="total-row">
                        <td className="col-label">Total Value</td>
                    </tr>
                </tbody>
            </table>

        </div>
    );
};

const VvConnectFeesLabelsColumn = ({ showFairValue }: { showFairValue: boolean }) => {
    return (
        <div className="loi-labels-column">
            <table className="vv-connect-fees-table">
                <thead>
                    <tr>
                        <th className="col-label section-title">VV Connect Fees</th>
                    </tr>
                </thead>
                <tbody>
                    {getFeeRows(showFairValue).map(({ label, key, isBold }) => (
                        <tr key={key} className={isBold ? 'total-row' : undefined}>
                            <td className="col-label">{label}</td>
                        </tr>
                    ))}
                </tbody>
            </table>
        </div>
    );
};

interface BuyerSummaryCardProps {
    item: LoiComparisonFormDto;
    showFairValue: boolean;
    collapsibleColumns: Record<string, boolean>;
    onToggleColumn: (key: string) => void;
    contractExplanationReservedKeys: Set<string>;
    onBuyerClick: () => void;
}

const BuyerSummaryCard = ({ item, showFairValue, collapsibleColumns, onToggleColumn, contractExplanationReservedKeys, onBuyerClick }: BuyerSummaryCardProps) => {
    const totalCells = computeTotalValues(item.form?.termForm?.purchasePriceSummary);
    const totalFaceValue = totalCells.faceValue ?? 0;
    const totalFairValue = totalCells.fairValue ?? 0;

    const contracts = item.form?.termForm?.assumptionOfMajorContracts;
    const nonComp = item.form?.termForm?.nonCompetitionRestriction;

    const amcComments = CONTRACT_LABELS
        .map((_, i) => getFieldComment(item, `amc:${i}`))
        .filter(Boolean);
    const amcComment = amcComments.length > 0 ? amcComments.join('\n') : undefined;

    const ncrComments = NON_COMP_LABELS
        .map((_, i) => getFieldComment(item, `ncr:${i}`))
        .filter(Boolean);
    const ncrComment = ncrComments.length > 0 ? ncrComments.join('\n') : undefined;

    const columnHeaders = [
        'Face Values',
        `${COLLAPSIBLE_COLUMN_TOKEN}face`,
        ...(showFairValue ? ['Fair Value', `${COLLAPSIBLE_COLUMN_TOKEN}fair`] : []),
        ...TAIL_COLUMN_HEADERS,
    ];
    const colSpan = columnHeaders.length;

    return (
        <div className="buyer-summary-card">
            <table>
                <thead>
                    <tr>
                        <th colSpan={colSpan} className="buyer-group-header">
                            <span className="clickable-text" onClick={onBuyerClick}>
                                {item.buyerName}
                            </span>
                        </th>
                    </tr>
                    <tr className="sub-col-header-row">
                        {columnHeaders.map((label, idx) => {
                            if (label.startsWith(COLLAPSIBLE_COLUMN_TOKEN)) {
                                const pctKey = label.slice(COLLAPSIBLE_COLUMN_TOKEN.length);
                                const visible = collapsibleColumns[pctKey];
                                return visible ? (
                                    <th key={`${label}-${idx}`} className="sub-col-header">
                                        <button
                                            className="percent-toggle-btn"
                                            onClick={() => onToggleColumn(pctKey)}
                                            aria-label="Collapse % column"
                                        >
                                            <FontAwesomeIcon icon={faMinus} />
                                        </button>
                                        % of Total
                                    </th>
                                ) : (
                                    <th key={`${label}-${idx}`} className="percent-expander-col">
                                        <button
                                            className="percent-toggle-btn"
                                            onClick={() => onToggleColumn(pctKey)}
                                            aria-label="Expand % column"
                                        >
                                            <FontAwesomeIcon icon={faPlus} />
                                        </button>
                                    </th>
                                );
                            }
                            return <th key={`${label}-${idx}`} className="sub-col-header">{label}</th>;
                        })}
                    </tr>
                </thead>
                <tbody>
                    {PRICE_ROWS.map(({ label, getCells, indented }) => {
                        const cells = getCells(item);
                        return (
                            <PriceRow
                                key={label}
                                label={label}
                                indented={indented}
                                cells={cells}
                                totalFaceValue={totalFaceValue}
                                totalFairValue={totalFairValue}
                                showFairValue={showFairValue}
                                showFaceColumn={collapsibleColumns.face}
                                showFairColumn={collapsibleColumns.fair}
                                buyerId={item.buyerId}
                                comment={getFieldComment(item, cells.fieldKey)}
                            />
                        );
                    })}
                    <PriceRow
                        label="Total Value"
                        cells={totalCells}
                        totalFaceValue={totalFaceValue}
                        totalFairValue={totalFairValue}
                        showFairValue={showFairValue}
                        showFaceColumn={collapsibleColumns.face}
                        showFairColumn={collapsibleColumns.fair}
                        buyerId={item.buyerId}
                        isBold
                    />
                </tbody>
            </table>

            <div className="section-header">
                <span>Assumption of Major Contracts</span>
                {amcComment && (
                    <InfoTooltip
                        text={amcComment}
                        idText={`amc-comment-${item.buyerId}`}
                    />
                )}
            </div>
            <div className="compact-grid compact-grid--contracts">
                {CONTRACT_LABELS.map(({ label }) => (
                    <span key={`h-${label}`} className="compact-grid__header">{label}</span>
                ))}
                {CONTRACT_LABELS.map(({ label, key, explanationKey }) => {
                    const explanation = contracts?.[explanationKey];
                    const shouldReserveExplanation = contractExplanationReservedKeys.has(explanationKey);
                    return (
                        <div key={`v-${label}`} className="compact-grid__value">
                            <span>{formatBoolean(contracts?.[key])}</span>
                            {shouldReserveExplanation && (
                                <span className="compact-grid__explanation">
                                    {explanation ? explanation : ' '}
                                </span>
                            )}
                        </div>
                    );
                })}
            </div>

            <div className="section-header">
                <span>Non-Competition Restrictions</span>
                {ncrComment && (
                    <InfoTooltip
                        text={ncrComment}
                        idText={`ncr-comment-${item.buyerId}`}
                    />
                )}
            </div>
            <div className="compact-grid compact-grid--non-comp">
                {NON_COMP_LABELS.map(({ label }) => (
                    <span key={`h-${label}`} className="compact-grid__header">{label}</span>
                ))}
                {NON_COMP_LABELS.map(({ label, key }) => (
                    <span key={`v-${label}`} className="compact-grid__value">
                        {formatCell(nonComp?.[key])}
                    </span>
                ))}
            </div>

        </div>
    );
};

export interface LoiComparisonSummaryProps {
    items: LoiComparisonFormDto[];
    levelOfService: number;
}

export const LoiComparisonSummary = ({ items, levelOfService }: LoiComparisonSummaryProps) => {
    const [selectedBuyerId, setSelectedBuyerId] = useState<number | null>(null);
    const [collapsibleColumns, setCollapsibleColumns] = useState<Record<string, boolean>>({ face: false, fair: false });
    const showFairValue = levelOfService === LevelOfService.FullServiceBrokerage;

    const toggleColumn = (key: string) => setCollapsibleColumns(prev => ({ ...prev, [key]: !prev[key] }));

    if (items.length === 0) {
        return null;
    }

    const contractExplanationReservedKeys = new Set<string>();
    CONTRACT_LABELS.forEach(({ explanationKey }) => {
        const anyBuyerHasExplanation = items.some((dto) => {
            const explanation = dto.form?.termForm?.assumptionOfMajorContracts?.[explanationKey];
            return typeof explanation === 'string' && explanation.trim().length > 0;
        });
        if (anyBuyerHasExplanation) {
            contractExplanationReservedKeys.add(explanationKey);
        }
    });

    return (
        <div className="loi-comparison-summary">
            <div className="summary-inner">
                <LabelsColumn />
                <div className="buyers-data-area">
                    {items.map((dto, index) => (
                        <React.Fragment key={dto.buyerId}>
                            {index > 0 && <div className="buyer-separator" />}
                            <BuyerSummaryCard
                                item={dto}
                                showFairValue={showFairValue}
                                collapsibleColumns={collapsibleColumns}
                                onToggleColumn={toggleColumn}
                                contractExplanationReservedKeys={contractExplanationReservedKeys}
                                onBuyerClick={() => setSelectedBuyerId(dto.buyerId)}
                            />
                        </React.Fragment>
                    ))}
                </div>
            </div>
            <div className="summary-inner vv-fees-section">
                <VvConnectFeesLabelsColumn showFairValue={showFairValue} />
                <div className="buyers-data-area">
                    {items.map((dto, index) => (
                        <React.Fragment key={dto.buyerId}>
                            {index > 0 && <div className="buyer-separator" />}
                            <div className="buyer-summary-card">
                                <VvConnectFeesSection fees={dto.vvConnectFees} />
                            </div>
                        </React.Fragment>
                    ))}
                </div>
            </div>
            {selectedBuyerId != null && (
                <BuyerProfilePreviewPopup
                    buyerId={selectedBuyerId}
                    close={() => setSelectedBuyerId(null)}
                />
            )}
        </div>
    );
};
